<template>
  <div class="cf-progress-bar-component cf-flex cf-flex--column cf-flex--gap-8 cf-padding-right-8">
    <div class="cf-progress-bar-container">
      <div class="cf-progress-bar cf-progress-bar--threshold">
        <div
          class="cf-progress-bar__fill"
          :style="{ width: `${progressPercentage}%` }"
        />

        <!-- Threshold markers -->
        <div
          v-for="threshold in thresholds"
          :key="threshold.id"
          class="cf-threshold-marker"
          :class="{ 'cf-threshold-marker--completed': threshold.isCompleted }"
          :style="{ left: `${calculateThresholdPosition(threshold.value)}%` }"
        >
          <div class="cf-threshold-dot">
            <span class="cf-threshold-amount">
              {{ formatValue(threshold.value) }}
            </span>
          </div>
        </div>

        <!-- Current progress indicator (prominent circle) -->
        <div
          v-if="showCurrentIndicator"
          class="cf-current-progress-indicator"
          :style="{ left: `${progressPercentage}%` }"
        >
          <div class="cf-current-progress-circle">
            <span class="cf-current-progress-amount">
              {{ formatCurrent(currentValue) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'CfProgressBar',

    props: {
      // Core progress data
      currentValue: {
        type: Number,
        required: true
      },
      maxValue: {
        type: Number,
        required: true
      },
      minValue: {
        type: Number,
        default: 0
      },

      // Thresholds array
      thresholds: {
        type: Array,
        default: () => []
        // Format: [{ value: 10, isCompleted: true, id: '1' }, { value: 50, isCompleted: false, id: '2' }]
      },

      // Display options
      showCurrentIndicator: {
        type: Boolean,
        default: true
      },

      // Value formatting function for threshold markers.
      formatValue: {
        type: Function,
        default: value => `${value}`
      },

      // Optional separate formatter for the current-progress indicator.
      // Useful when threshold labels carry a unit (e.g. "15 pts") but the
      // current indicator should show only the number. Falls back to
      // `formatValue` when null.
      formatCurrentValue: {
        type: Function,
        default: null
      }
    },

    computed: {
      progressPercentage() {
        if (!this.maxValue) return 100;
        const range = this.maxValue - this.minValue;
        const progress = this.currentValue - this.minValue;
        return Math.min((progress / range) * 100, 100);
      }
    },

    methods: {
      calculateThresholdPosition(thresholdValue) {
        if (!this.maxValue) return 0;
        const range = this.maxValue - this.minValue;
        const progress = thresholdValue - this.minValue;
        return Math.max(0, Math.min(100, (progress / range) * 100));
      },

      formatCurrent(value) {
        const formatter = this.formatCurrentValue || this.formatValue;
        return formatter(value);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .cf-progress-bar-component {
    .cf-progress-bar-container {
      position: relative;
      height: 20px;
    }

    .cf-progress-bar {
      width: 100%;
      height: 8px;
      background-color: var(--color-light-gray);
      border-radius: 4px;
      position: relative;
      overflow: visible;

      &__fill {
        height: 100%;
        background-color: var(--color-brand-primary);
        border-radius: 4px;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: width 0.3s ease;
      }

      &__amount {
        color: var(--color-light);
        font-size: 10px;
        font-weight: bold;
        position: absolute;
      }
    }

    .cf-threshold-marker {
      position: absolute;
      top: -6px;
      transform: translateX(-50%);

      .cf-threshold-dot {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: var(--color-gray);
        border: 1px solid var(--color-light);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &--completed .cf-threshold-dot {
        background-color: var(--color-brand-primary);
      }

      .cf-threshold-amount {
        font-size: 7px;
        font-weight: 700;
        color: var(--color-light);
        line-height: 1;
        text-align: center;
      }
    }

    .cf-current-progress-indicator {
      position: absolute;
      top: -10px;
      transform: translateX(-50%);
      z-index: 10;

      .cf-current-progress-circle {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background-color: var(--color-brand-primary);
        border: 1px solid var(--color-dark);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .cf-current-progress-amount {
        font-size: 8px;
        font-weight: 700;
        color: var(--color-light);
        line-height: 1;
      }
    }
  }
</style>
